import { showToast } from "@/components/feedback";
import { sameCityCheckEnabled } from "@/consts";
import { hasCepCoordinates, lookupCep } from "@/lib/cep";
import { reverseGeocode, type GoogleGeocodingResponse, type GoogleGeocodingResult } from "@/lib/geocoding";
import { AddressSchemaType } from "@/types/forms";
import { useRef, useState } from "react";
import { UseFormSetValue } from "react-hook-form";

/** Accent-insensitive, case-insensitive comparison key for city/state names. */
function normalizeText(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim()
}

export type CityMismatch = { mismatch: boolean; city: string; state: string }

/**
 * The "entry" step's two ways to seed a position: GPS (one reverse geocode)
 * or CEP (free lookup APIs). Owns the position/mismatch state and refs that
 * both this step and the later map/save step read, so the wizard has a
 * single source for "where did this address's coordinates come from".
 */
export function useAddressEntryGeocoding({
  isNeighborhoodMode,
  storeCity,
  storeState,
  setLoading,
  setValue,
  setStep,
}: {
  isNeighborhoodMode: boolean
  storeCity: string
  storeState: string
  setLoading: (loading: boolean) => void
  setValue: UseFormSetValue<AddressSchemaType>
  setStep: (step: "entry" | "details" | "map") => void
}) {
  const [position, setPosition] = useState<{ lat: number; lng: number }>()
  const [cityMismatch, setCityMismatch] = useState<CityMismatch>({
    mismatch: false,
    city: "",
    state: "",
  })

  const lastCepRef = useRef<string>("")
  /**
   * Coords from CEP lookup (or GPS). Kept so "Continuar para o mapa" can try a
   * better forward-geocode of the filled street address and fall back here if
   * free geocoders cannot refine (common for DF superquadras / city centroids).
   */
  const fallbackPositionRef = useRef<{ lat: number; lng: number } | null>(null)
  /** Where the fallback position came from: GPS is street-level, a CEP point is only its area. */
  const fallbackSourceRef = useRef<"gps" | "cep" | null>(null)

  function applyReverseGeocodeToForm(data: GoogleGeocodingResponse): boolean {
    if (data.results.length === 0) return false
    const result: GoogleGeocodingResult = data.results[0]
    const getComponent = (targetType: string, key: "long_name" | "short_name"): string => {
      const component = result.address_components.find((c) => c.types.includes(targetType))
      return component?.[key] ?? ""
    }

    const district = (
      getComponent("neighborhood", "long_name") ||
      getComponent("sublocality", "long_name") ||
      getComponent("sublocality_level_1", "long_name")
    ).trim()
    const street = (
      getComponent("route", "long_name") ||
      getComponent("street_address", "long_name")
    ).trim()
    const number = getComponent("street_number", "long_name").trim()
    const city = (
      getComponent("administrative_area_level_2", "long_name") ||
      getComponent("locality", "long_name")
    ).trim()
    const stateShort = getComponent("administrative_area_level_1", "short_name").trim()
    const stateLong = getComponent("administrative_area_level_1", "long_name").trim()
    const postal = getComponent("postal_code", "long_name")
    const formattedPostal =
      postal.replace(/\D/g, "").length === 8
        ? `${postal.replace(/\D/g, "").slice(0, 5)}-${postal.replace(/\D/g, "").slice(5)}`
        : postal

    if (district && !isNeighborhoodMode) setValue("district", district)
    if (street) setValue("street", street)
    if (number) setValue("number", number)
    if (city) setValue("city", city)
    else if (storeCity) setValue("city", storeCity)
    if (stateShort) setValue("state", stateShort)
    else if (storeState) setValue("state", storeState)
    if (formattedPostal) {
      setValue("postalCode", formattedPostal)
      lastCepRef.current = formattedPostal
    }
    setValue("complement", "")

    let isMismatch = false
    if (isNeighborhoodMode && (storeCity || storeState)) {
      const cityDiffers =
        sameCityCheckEnabled && !!city && !!storeCity && normalizeText(city) !== normalizeText(storeCity)
      const storeStateNorm = normalizeText(storeState)
      const stateDiffers =
        !!storeState &&
        !!stateShort &&
        storeStateNorm !== normalizeText(stateShort) &&
        storeStateNorm !== normalizeText(stateLong)
      isMismatch = cityDiffers || stateDiffers
      setCityMismatch({ mismatch: isMismatch, city: storeCity, state: storeState })
    } else {
      setCityMismatch({ mismatch: false, city: "", state: "" })
    }

    const location = result.geometry.location
    setValue("latitude", location.lat)
    setValue("longitude", location.lng)
    return isMismatch
  }

  async function onGeoSuccess(geo: GeolocationPosition) {
    const latitude = geo.coords.latitude
    const longitude = geo.coords.longitude
    if (geo.coords.accuracy > 200) {
      showToast.error("A localização ficou imprecisa. Tente de novo ou use o CEP.")
      setLoading(false)
      return
    }

    try {
      const data = await reverseGeocode(latitude, longitude)
      if (data.status !== "OK" || data.results.length === 0) {
        showToast.error("Não conseguimos identificar seu endereço. Tente o CEP.")
        setLoading(false)
        return
      }
      const hasMismatch = applyReverseGeocodeToForm(data)
      if (hasMismatch) {
        showToast.error(
          `Sua localização fica fora da área da loja (${[storeCity, storeState].filter(Boolean).join(" - ")}).`,
        )
        setLoading(false)
        return
      }
      const coords = { lat: latitude, lng: longitude }
      fallbackPositionRef.current = coords
      fallbackSourceRef.current = "gps"
      setPosition(coords)
      setStep("details")
    } catch {
      showToast.error("Não deu pra buscar sua localização. Tente o CEP.")
    } finally {
      setLoading(false)
    }
  }

  function onGeoError() {
    showToast.error("Não conseguimos achar sua localização. Verifique se o GPS está ligado.")
    setLoading(false)
  }

  function handleLocation() {
    setLoading(true)
    if (!navigator.geolocation) {
      showToast.error("Seu navegador não suporta GPS. Use o CEP.")
      setLoading(false)
      return
    }
    navigator.geolocation.getCurrentPosition(onGeoSuccess, onGeoError, {
      enableHighAccuracy: true,
      timeout: 10000,
    })
  }

  const fetchCoordsByCep = async (cep: string) => {
    setLoading(true)
    setCityMismatch({ mismatch: false, city: "", state: "" })
    try {
      const cepData = await lookupCep(cep)
      if (!cepData) {
        showToast.error("Não achamos esse CEP. Dá uma conferida nos números.")
        return
      }

      if (isNeighborhoodMode && (storeCity || storeState)) {
        const cityDiffers =
          sameCityCheckEnabled &&
          !!cepData.city &&
          !!storeCity &&
          normalizeText(cepData.city) !== normalizeText(storeCity)
        const stateDiffers =
          !!storeState && !!cepData.state && normalizeText(storeState) !== normalizeText(cepData.state)
        if (cityDiffers || stateDiffers) {
          setCityMismatch({ mismatch: true, city: storeCity, state: storeState })
          return
        }
      }

      setValue("postalCode", cep)
      if (cepData.street) setValue("street", cepData.street)
      if (!isNeighborhoodMode && cepData.district) setValue("district", cepData.district)
      setValue("city", cepData.city || storeCity)
      setValue("state", cepData.state || storeState)
      setValue("complement", "")

      if (hasCepCoordinates(cepData)) {
        const coords = { lat: cepData.latitude!, lng: cepData.longitude! }
        fallbackPositionRef.current = coords
        fallbackSourceRef.current = "cep"
        setPosition(coords)
        setValue("latitude", coords.lat)
        setValue("longitude", coords.lng)
      } else {
        fallbackPositionRef.current = null
        fallbackSourceRef.current = null
      }

      setStep("details")
    } catch (error) {
      console.error("Erro ao buscar CEP:", error)
      showToast.error("Não deu pra buscar o CEP agora. Tente de novo.")
    } finally {
      setLoading(false)
    }
  }

  return {
    position,
    setPosition,
    cityMismatch,
    setCityMismatch,
    lastCepRef,
    fallbackPositionRef,
    fallbackSourceRef,
    handleLocation,
    fetchCoordsByCep,
  }
}
